import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { FILTER_OPTIONS } from "../constants";
import { changeOrganization } from "../redux/organizationsSagas";
import { organizationsApi } from "../api";
import {
  filterByDefault,
  filterByValue,
  mapByName,
  schemaResolver,
  unregisterFields
} from "../utils";

export const useOrganizationEdit = ({ id }) => {
  const dispatch = useDispatch();

  const { locationalOverviewReportMetrics, dateFormats, timeFormats, global2fa } = useSelector(
    (state) => state.organizations.commonSettings
  );

  const [currentOrganizationInfo, setCurrentOrganizationInfo] = useState({});

  const getDefaultValues = async () => {
    let { data } = await organizationsApi.getOrganizationInfo({ params: { id: id.toString() } });

    const {
      businessName,
      category,
      inactive,
      activeYears,
      deactivateOn,
      currency,
      dateFormat,
      timeFormat,
      roles,
      beginningOfWeek,
      clientName,
      inactiveUsers,
      use2fa,
      isDemo,
      availableGoals,
      availableStaffGoals,
      defaultDashboardModules,
      useSecondaryRevenueCategories,
      zeroRetention,
      exclude_client_prospect_types,
      countSameDayConversion,
      defaultConversionRevenueTypes,
      showFutureAppointmentsOnFunnelLaser,
      showFutureAppointmentsByDefault,
      calculateHistoricalData,
      calculateChallengers,
      showLaserNetworkAverages,
      leaderboardDetails,
      daysFromTheLastVisit,
      daysPreviousToTheLastVisit,
      availablePages,
      locationalOverviewReportMetrics: selectedLocationalOverviewReportMetrics,
      dailyEmailNotifierClass,
      sendDailyReportEmails,
      includeCrossRegionalVisits,
      startFromAppointmentByDefault,
      excludePreconvertedClientsFromAppointments,
      exclude_frozen_members_from_total_members_count,
      attritionRateLogic,
      dailyPulseAttritionRateLogic,
      membershipCalculationAgreementTypes,
      membershipMetric: selectedMembershipMetric,
      agreementNamesWithZeroSales,
      membershipStart
    } = data;

    const defaultValues = {
      business_name: businessName,
      category: filterByValue(FILTER_OPTIONS.categoryList, category),
      inactive,
      active_years: mapByName(activeYears),
      deactivate_on: deactivateOn,
      currency,
      date_format: filterByValue(dateFormats, dateFormat),
      time_format: filterByValue(timeFormats, timeFormat),
      default_client_role_id: filterByDefault(roles, "isDefault")[0] ?? null,
      beginning_of_week: filterByValue(FILTER_OPTIONS.beginningOfWeek, beginningOfWeek),

      client_name: clientName,
      inactive_clients: inactiveUsers,
      use_2fa: use2fa,
      is_demo: isDemo,

      available_goals: availableGoals,
      available_staff_goals: availableStaffGoals,

      default_dashboard_modules: filterByDefault(defaultDashboardModules),

      use_secondary_revenue_categories: useSecondaryRevenueCategories,

      exclude_client_prospect_types,
      zero_retention: zeroRetention,
      count_same_day_conversion: countSameDayConversion,
      default_conversion_revenue_types: mapByName(defaultConversionRevenueTypes),
      show_future_appointments_on_funnel_laser: showFutureAppointmentsOnFunnelLaser,
      show_future_appointments_by_default: showFutureAppointmentsByDefault,

      calculate_historical_data: calculateHistoricalData,
      calculate_challengers: calculateChallengers,
      show_laser_network_averages: showLaserNetworkAverages,
      leaderboard_details: leaderboardDetails,
      risk_members_days_from_last_visit: daysFromTheLastVisit,
      risk_members_days_prev_last_visit: daysPreviousToTheLastVisit,

      available_pages: mapByName(availablePages),
      locational_overview_report_metrics: locationalOverviewReportMetrics.filter((item) =>
        selectedLocationalOverviewReportMetrics.includes(item.id)
      ),

      daily_email_notifier_class: dailyEmailNotifierClass,
      send_daily_report_emails: sendDailyReportEmails,
      include_cross_regional_visits: includeCrossRegionalVisits,
      start_from_appointment_by_default: startFromAppointmentByDefault,
      exclude_preconverted_clients_from_appointments: excludePreconvertedClientsFromAppointments,
      exclude_frozen_members_from_total_members_count:
        exclude_frozen_members_from_total_members_count,
      attrition_rate_logic: filterByValue(FILTER_OPTIONS.attritionRateLogic, attritionRateLogic),
      daily_pulse_attrition_rate_logic: filterByValue(
        FILTER_OPTIONS.attritionRateLogic,
        dailyPulseAttritionRateLogic
      ),
      membership_calculation_agreement_types: mapByName(membershipCalculationAgreementTypes),

      membership_metric: filterByValue(FILTER_OPTIONS.membershipMetrics, selectedMembershipMetric),
      agreement_names_with_zero_sales: filterByDefault(agreementNamesWithZeroSales),
      membership_start: filterByValue(FILTER_OPTIONS.membershipStart, membershipStart)
    };

    setCurrentOrganizationInfo({
      agreementNamesWithZeroSales,
      roles
    });

    return defaultValues;
  };

  const form = useForm({
    resolver: schemaResolver,
    defaultValues: getDefaultValues
  });

  let {
    handleSubmit,
    watch,
    unregister,
    formState: { isLoading, dirtyFields }
  } = form;

  const watchedValues = watch(["show_future_appointments_on_funnel_laser", "membership_metric"]);
  const [showFutureAppointments, membershipMetric] = watchedValues;

  const onSubmit = async (data) => {
    dispatch(changeOrganization({ id, data, dirtyFields }));
  };

  useEffect(() => {
    if (isLoading) return;

    unregisterFields({ unregister, showFutureAppointments, membershipMetric, global2fa });

    if (
      membershipMetric.id === "NumberOfMembersByProfile" ||
      membershipMetric.id === "NumberOfMembersByStatus"
    ) {
      unregister("agreement_names_with_zero_sales", { keepDefaultValue: true });
    }
  }, [showFutureAppointments, membershipMetric]);

  return {
    ...form,
    watchedValues,
    submit: handleSubmit(onSubmit),
    currentOrganizationInfo,
    setCurrentOrganizationInfo
  };
};
